import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const fadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 }
};

const slideData = [
  { id: "slide1", title: "Blueberry Pancakes", image: "/images/hero/blueberry-pancakes-original-pancake-house.jpg", alt: "Delicious blueberry pancakes" },
  { id: "slide2", title: "Cornflake-Crusted French Toast", image: "/images/hero/french-toast-original-pancake-house-hero.jpg", alt: "French toast at the Original Pancake House" },
  { id: "slide3", title: "Country Benedict", image: "/images/hero/country-benedict-oph.jpg", alt: "Country Benedict at the Original Pancake House" }
];

export default function HeroSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  const animationDuration = 8; // seconds for the full animation cycle
  
  const slideIndex = Math.abs(currentIndex % slideData.length);
  
  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => prev + 1);
      }, animationDuration * 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying]);
  
  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(prev => prev + 1);
    // Restart autoplay after manual navigation
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + 1);
    }, animationDuration * 1000);
  };
  
  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(prev => prev - 1);
    // Restart autoplay after manual navigation
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + 1);
    }, animationDuration * 1000);
  };
  
  // Auto-advance slides
  useEffect(() => {
    // This effect handles auto-advancing slides
  }, [currentIndex]);
  
  return (
    <div className="relative w-full h-full rounded-t-lg rounded-b-lg sm:rounded-b-none overflow-hidden">
      {/* Images with crossfade */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentIndex}
          variants={fadeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            opacity: { duration: 1.0 }
          }}
          className="absolute w-full h-full"
        >
          <img 
            src={slideData[slideIndex].image} 
            alt={slideData[slideIndex].alt} 
            className="w-full h-full object-cover object-center sm:object-top rounded-t-lg rounded-b-lg sm:rounded-b-none"
            fetchPriority={slideIndex === 0 ? "high" : "auto"}
            loading={slideIndex === 0 ? "eager" : "lazy"}
          />
          
          {/* Navy gradient overlay at top - taller on mobile */}
          <div className="absolute inset-x-0 top-0 h-1/2 sm:h-1/4 bg-gradient-to-b from-brand-navy to-transparent opacity-75"></div>
          
          {/* Navy gradient overlay at bottom - taller on mobile */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 sm:h-1/4 bg-gradient-to-t from-brand-navy to-transparent opacity-75"></div>
        </motion.div>
      </AnimatePresence>
      
      {/* Featured Slide Content - Large heading and subheading in bottom left */}
      {slideData[slideIndex].featured && (
        <div className="absolute bottom-8 left-4 sm:left-8 z-10 max-w-xl">
          <AnimatePresence initial={false} mode="wait">
            <motion.div 
              key={`featured-${currentIndex}`}
              variants={fadeVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                opacity: { duration: 0.5 }
              }}
            >
              <h2 className="text-brand-cream font-heading text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4 leading-tight">
                {slideData[slideIndex].title}
              </h2>
              {slideData[slideIndex].subtitle && (
                <p className="text-brand-cream text-base sm:text-lg md:text-xl font-medium leading-relaxed">
                  {slideData[slideIndex].subtitle}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      
      {/* Navigation Arrows with Title to the right - positioned lower in the image (only for non-featured slides) */}
      {!slideData[slideIndex].featured && (
        <div className="absolute bottom-6 left-4 z-10 flex items-center space-x-4">
          {/* Navigation arrows */}
          <div className="flex space-x-2 sm:space-x-4">
            <button 
              onClick={prevSlide}
              className="text-brand-cream hover:text-brand-yellow transition-colors p-1"
              aria-label="Previous slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={nextSlide}
              className="text-brand-cream hover:text-brand-yellow transition-colors p-1"
              aria-label="Next slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {/* Slide Title - fixed position to the right of arrows */}
          <div className="text-brand-cream ml-4" style={{ width: "150px" }}>
            <AnimatePresence initial={false} mode="wait">
              <motion.div 
                key={`title-${currentIndex}`}
                variants={fadeVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  opacity: { duration: 0.5 }
                }}
              >
                <p className="text-xs sm:text-sm font-medium">{slideData[slideIndex].title}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
      
      {/* Navigation Arrows for Featured Slides - positioned in bottom right */}
      {slideData[slideIndex].featured && (
        <div className="absolute bottom-6 right-4 sm:right-8 z-10 flex space-x-2 sm:space-x-4">
          <button 
            onClick={prevSlide}
            className="text-brand-cream hover:text-brand-yellow transition-colors p-1"
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={nextSlide}
            className="text-brand-cream hover:text-brand-yellow transition-colors p-1"
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
      
    </div>
  );
}